import { useState } from 'react';

import {
  createJob,
  updateJob,
  deleteJob
} from '../services/jobService';

const EMPTY_FORM = {
  title: '',
  company: '',
  location: 'Spruce Grove, AB',
  city: 'Spruce Grove',
  province: 'AB',
  salary: '',
  hoursPerWeek: '',
  employmentType: '',
  workplaceType: 'On site',
  description: '',

  languages: 'English',
  education: '',
  experience: '',
  workplaceDescription:
    'Work must be completed at the physical location. There is no option to work remotely.',
  workSetting: '',
  workSiteEnvironment: '',
  budgetaryResponsibility: '',

  tasks: '',
  supervision: '',

  computerAndTechnologyKnowledge: '',
  technicalTerminology: '',
  areaOfSpecialization: '',

  transportationTravelInformation: '',
  workConditionsAndPhysicalCapabilities: '',
  ownToolsEquipment: '',
  personalSuitability: '',
  securityAndSafety: '',
  weightHandling: '',

  financialBenefits: '',
  healthBenefits: '',
  longTermBenefits: '',
  otherBenefits: '',

  certificatesLicencesMembershipsCourses: ''
};

function lines(value) {

  return String(value || '')
    .split('\n')
    .map(item => item.trim())
    .filter(Boolean);
}

function joinValues(value) {

  if (Array.isArray(value)) {
    return value.join('\n');
  }

  return value || '';
}

function sectionValue(
  job,
  section,
  subheading
) {

  return joinValues(
    job.sections?.[section]?.[subheading]
  );
}

function buildSections(form) {

  return {
    OVERVIEW: {
      Languages: lines(form.languages),
      Education: lines(form.education),
      Experience: lines(form.experience),
      [form.workplaceType || 'On site']:
        lines(form.workplaceDescription),
      'Work setting':
        lines(form.workSetting),
      'Work site environment':
        lines(form.workSiteEnvironment),
      'Budgetary responsibility':
        lines(form.budgetaryResponsibility)
    },

    RESPONSIBILITIES: {
      Tasks: lines(form.tasks),
      Supervision: lines(form.supervision)
    },

    'EXPERIENCE AND SPECIALIZATION': {
      'Computer and technology knowledge':
        lines(form.computerAndTechnologyKnowledge),
      'Technical terminology':
        lines(form.technicalTerminology),
      'Area of specialization':
        lines(form.areaOfSpecialization)
    },

    'ADDITIONAL INFORMATION': {
      'Transportation/travel information':
        lines(form.transportationTravelInformation),
      'Work conditions and physical capabilities':
        lines(form.workConditionsAndPhysicalCapabilities),
      'Own tools/equipment':
        lines(form.ownToolsEquipment),
      'Personal suitability':
        lines(form.personalSuitability),
      'Security and safety':
        lines(form.securityAndSafety),
      'Weight handling':
        lines(form.weightHandling)
    },

    BENEFITS: {
      'Financial benefits':
        lines(form.financialBenefits),
      'Health benefits':
        lines(form.healthBenefits),
      'Long term benefits':
        lines(form.longTermBenefits),
      'Other benefits':
        lines(form.otherBenefits)
    },

    CREDENTIALS: {
      'Certificates, licences, memberships, and courses':
        lines(form.certificatesLicencesMembershipsCourses)
    }
  };
}

function formFromJob(job) {

  return {
    ...EMPTY_FORM,

    title:
      job.title || '',

    company:
      job.company || '',

    location:
      job.location ||
      [job.city, job.province]
        .filter(Boolean)
        .join(', ') ||
      EMPTY_FORM.location,

    city:
      job.city || 'Spruce Grove',

    province:
      job.province || 'AB',

    salary:
      job.salary || '',

    hoursPerWeek:
      job.hoursPerWeek || '',

    employmentType:
      job.employmentType || '',

    workplaceType:
      job.workplaceType ||
      job.type ||
      'On site',

    description:
      job.description || '',

    languages:
      sectionValue(job, 'OVERVIEW', 'Languages'),

    education:
      sectionValue(job, 'OVERVIEW', 'Education') ||
      job.education ||
      '',

    experience:
      sectionValue(job, 'OVERVIEW', 'Experience') ||
      job.experience ||
      '',

    workplaceDescription:
      sectionValue(
        job,
        'OVERVIEW',
        job.workplaceType || job.type || 'On site'
      ) ||
      EMPTY_FORM.workplaceDescription,

    workSetting:
      sectionValue(job, 'OVERVIEW', 'Work setting') ||
      job.workSetting ||
      '',

    workSiteEnvironment:
      sectionValue(job, 'OVERVIEW', 'Work site environment'),

    budgetaryResponsibility:
      sectionValue(job, 'OVERVIEW', 'Budgetary responsibility'),

    tasks:
      sectionValue(job, 'RESPONSIBILITIES', 'Tasks') ||
      joinValues(job.responsibilities),

    supervision:
      sectionValue(job, 'RESPONSIBILITIES', 'Supervision'),

    computerAndTechnologyKnowledge:
      sectionValue(
        job,
        'EXPERIENCE AND SPECIALIZATION',
        'Computer and technology knowledge'
      ) ||
      joinValues(job.technologySkills),

    technicalTerminology:
      sectionValue(
        job,
        'EXPERIENCE AND SPECIALIZATION',
        'Technical terminology'
      ),

    areaOfSpecialization:
      sectionValue(
        job,
        'EXPERIENCE AND SPECIALIZATION',
        'Area of specialization'
      ),

    transportationTravelInformation:
      sectionValue(
        job,
        'ADDITIONAL INFORMATION',
        'Transportation/travel information'
      ),

    workConditionsAndPhysicalCapabilities:
      sectionValue(
        job,
        'ADDITIONAL INFORMATION',
        'Work conditions and physical capabilities'
      ),

    ownToolsEquipment:
      sectionValue(
        job,
        'ADDITIONAL INFORMATION',
        'Own tools/equipment'
      ),

    personalSuitability:
      sectionValue(
        job,
        'ADDITIONAL INFORMATION',
        'Personal suitability'
      ) ||
      joinValues(job.suitability),

    securityAndSafety:
      sectionValue(
        job,
        'ADDITIONAL INFORMATION',
        'Security and safety'
      ),

    weightHandling:
      sectionValue(
        job,
        'ADDITIONAL INFORMATION',
        'Weight handling'
      ),

    financialBenefits:
      sectionValue(job, 'BENEFITS', 'Financial benefits'),

    healthBenefits:
      sectionValue(job, 'BENEFITS', 'Health benefits'),

    longTermBenefits:
      sectionValue(job, 'BENEFITS', 'Long term benefits'),

    otherBenefits:
      sectionValue(job, 'BENEFITS', 'Other benefits'),

    certificatesLicencesMembershipsCourses:
      sectionValue(
        job,
        'CREDENTIALS',
        'Certificates, licences, memberships, and courses'
      )
  };
}

function getDaysRemaining(expiryDate) {

  if (!expiryDate) {
    return '';
  }

  const expiry =
    expiryDate?.toDate
      ? expiryDate.toDate()
      : new Date(expiryDate);

  if (isNaN(expiry.getTime())) {
    return '';
  }

  const now =
    new Date();

  const diff =
    expiry - now;

  const days =
    Math.ceil(
      diff / (1000 * 60 * 60 * 24)
    );

  if (days <= 0) {
    return 'Expiring today';
  }

  return `${days} days remaining`;
}

function TextAreaField({
  label,
  value,
  onChange,
  rows = 5,
  placeholder = 'One item per line'
}) {

  return (
    <label className="field-label">

      <span>
        {label}
      </span>

      <textarea
        rows={rows}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
      />

    </label>
  );
}

export default function EmployerJobManager({
  jobs,
  reloadJobs
}) {

  const [form, setForm] = useState(EMPTY_FORM);

  const [editingJobId, setEditingJobId] = useState(null);

  function update(field, value) {

    setForm(prev => ({
      ...prev,
      [field]: value
    }));
  }

  async function submit(e) {

    e.preventDefault();

    const payload = {
      title: form.title,
      company: form.company,
      location: form.location,
      city: form.city,
      province: form.province,
      salary: form.salary,
      hoursPerWeek: form.hoursPerWeek,
      employmentType: form.employmentType,
      workplaceType: form.workplaceType,
      type: form.workplaceType,
      description: form.description,
      sections: buildSections(form),
      source: 'local'
    };

    let result;

    if (editingJobId) {

      result = await updateJob(
        editingJobId,
        payload
      );

      setEditingJobId(null);

    } else {

      result = await createJob(payload);
    }

    if (result.success) {

      reloadJobs();

      setForm(EMPTY_FORM);
    }
  }

  function editJob(job) {

    setEditingJobId(job.id);

    setForm(
      formFromJob(job)
    );
  }

  async function remove(id) {

    await deleteJob(id);

    reloadJobs();
  }

  return (
    <div className="employer-layout">

      <div className="employer-form-panel">

        <h2>
          Employer Dashboard
        </h2>

        <form onSubmit={submit}>

          <section className="form-section">

            <h3>
              Job Basics
            </h3>

            <div className="form-grid">
              <input
                value={form.company}
                onChange={(e) =>
                  update('company', e.target.value)
                }
                placeholder="Company"
              />
              <input
                value={form.title}
                onChange={(e) =>
                  update('title', e.target.value)
                }
                placeholder="Job title"
              />
              <input
                value={form.location}
                onChange={(e) =>
                  update('location', e.target.value)
                }
                placeholder="Location, e.g. Spruce Grove, AB"
              />
              <input
                value={form.salary}
                onChange={(e) =>
                  update('salary', e.target.value)
                }
                placeholder="Salary"
              />
              <input
                value={form.hoursPerWeek}
                onChange={(e) =>
                  update('hoursPerWeek', e.target.value)
                }
                placeholder="Hours per week"
              />
              <input
                value={form.employmentType}
                onChange={(e) =>
                  update('employmentType', e.target.value)
                }
                placeholder="Employment type, e.g. Full time"
              />
              <select
                value={form.workplaceType}
                onChange={(e) =>
                  update('workplaceType', e.target.value)
                }
              >
                <option value="On site">
                  On site
                </option>

                <option value="Hybrid">
                  Hybrid
                </option>

                <option value="On the road">
                  On the road
                </option>
              </select>
            </div>

            <TextAreaField
              label="Short Description"
              rows={4}
              value={form.description}
              onChange={(e) =>
                update('description', e.target.value)
              }
              placeholder="Optional short job description"
            />

          </section>

          <section className="form-section">

            <h3>
              OVERVIEW
            </h3>

            <div className="form-grid">

              <input
                value={form.languages}
                onChange={(e) =>
                  update('languages', e.target.value)
                }
                placeholder="Languages"
              />

              <input
                value={form.education}
                onChange={(e) =>
                  update('education', e.target.value)
                }
                placeholder="Education"
              />

              <input
                value={form.experience}
                onChange={(e) =>
                  update('experience', e.target.value)
                }
                placeholder="Experience"
              />

              <input
                value={form.workSetting}
                onChange={(e) =>
                  update('workSetting', e.target.value)
                }
                placeholder="Work setting"
              />

              <input
                value={form.workSiteEnvironment}
                onChange={(e) =>
                  update('workSiteEnvironment', e.target.value)
                }
                placeholder="Work site environment"
              />

              <input
                value={form.budgetaryResponsibility}
                onChange={(e) =>
                  update('budgetaryResponsibility', e.target.value)
                }
                placeholder="Budgetary responsibility"
              />

            </div>

            <TextAreaField
              label={`${form.workplaceType} Details`}
              rows={3}
              value={form.workplaceDescription}
              onChange={(e) =>
                update('workplaceDescription', e.target.value)
              }
            />

          </section>

          <section className="form-section">

            <h3>
              RESPONSIBILITIES
            </h3>

            <TextAreaField
              label="Tasks"
              rows={10}
              value={form.tasks}
              onChange={(e) =>
                update('tasks', e.target.value)
              }
            />

            <TextAreaField
              label="Supervision"
              rows={3}
              value={form.supervision}
              onChange={(e) =>
                update('supervision', e.target.value)
              }
            />

          </section>

          <section className="form-section">

            <h3>
              EXPERIENCE AND SPECIALIZATION
            </h3>

            <TextAreaField
              label="Computer and Technology Knowledge"
              value={form.computerAndTechnologyKnowledge}
              onChange={(e) =>
                update(
                  'computerAndTechnologyKnowledge',
                  e.target.value
                )
              }
            />

            <TextAreaField
              label="Technical Terminology"
              rows={3}
              value={form.technicalTerminology}
              onChange={(e) =>
                update('technicalTerminology', e.target.value)
              }
            />

            <TextAreaField
              label="Area of Specialization"
              value={form.areaOfSpecialization}
              onChange={(e) =>
                update('areaOfSpecialization', e.target.value)
              }
            />

          </section>

          <section className="form-section">

            <h3>
              ADDITIONAL INFORMATION
            </h3>

            <TextAreaField
              label="Transportation / Travel Information"
              value={form.transportationTravelInformation}
              onChange={(e) =>
                update(
                  'transportationTravelInformation',
                  e.target.value
                )
              }
            />

            <TextAreaField
              label="Work Conditions and Physical Capabilities"
              value={form.workConditionsAndPhysicalCapabilities}
              onChange={(e) =>
                update(
                  'workConditionsAndPhysicalCapabilities',
                  e.target.value
                )
              }
            />

            <TextAreaField
              label="Own Tools / Equipment"
              value={form.ownToolsEquipment}
              onChange={(e) =>
                update('ownToolsEquipment', e.target.value)
              }
            />

            <TextAreaField
              label="Personal Suitability"
              value={form.personalSuitability}
              onChange={(e) =>
                update('personalSuitability', e.target.value)
              }
            />

            <TextAreaField
              label="Security and Safety"
              value={form.securityAndSafety}
              onChange={(e) =>
                update('securityAndSafety', e.target.value)
              }
            />

            <TextAreaField
              label="Weight Handling"
              rows={3}
              value={form.weightHandling}
              onChange={(e) =>
                update('weightHandling', e.target.value)
              }
            />

          </section>

          <section className="form-section">

            <h3>
              BENEFITS
            </h3>

            <TextAreaField
              label="Financial Benefits"
              value={form.financialBenefits}
              onChange={(e) =>
                update('financialBenefits', e.target.value)
              }
            />

            <TextAreaField
              label="Health Benefits"
              value={form.healthBenefits}
              onChange={(e) =>
                update('healthBenefits', e.target.value)
              }
            />

            <TextAreaField
              label="Long Term Benefits"
              value={form.longTermBenefits}
              onChange={(e) =>
                update('longTermBenefits', e.target.value)
              }
            />

            <TextAreaField
              label="Other Benefits"
              value={form.otherBenefits}
              onChange={(e) =>
                update('otherBenefits', e.target.value)
              }
            />

          </section>

          <section className="form-section">

            <h3>
              CREDENTIALS
            </h3>

            <TextAreaField
              label="Certificates, Licences, Memberships, and Courses"
              value={form.certificatesLicencesMembershipsCourses}
              onChange={(e) =>
                update(
                  'certificatesLicencesMembershipsCourses',
                  e.target.value
                )
              }
            />

          </section>

          <button
            className="post-job-button"
            type="submit"
          >
            {
              editingJobId
                ? 'Save Changes'
                : 'Post Job'
            }
          </button>

        </form>

      </div>

      <div className="employer-jobs-panel">

        <h2>
          Your Jobs
        </h2>

        {
          jobs.map(job => (
            <div
              className="employer-job-card"
              key={job.id}
            >

              <h3>
                {job.title}
              </h3>

              <p>
                {job.company}
              </p>
              
              <p>{job.location}</p>

              <p>
                <strong>
                  Applications:
                </strong>
                {job.applications || 0}
              </p>

              <div className="employer-job-meta">

                <p>
                  <strong>
                    Posted:
                  </strong>{' '}

                  {
                    job.postedDate
                      ?.toDate()
                      ?.toLocaleString()
                  }
                </p>

                <p>
                  <strong>
                    Expires:
                  </strong>{' '}

                  {
                    getDaysRemaining(
                      job.expiryDate
                    )
                  }
                </p>

              </div>

              <div className="employer-job-actions">

                <button
                  className="edit-job-button"
                  onClick={() => editJob(job)}
                >
                  Edit Job
                </button>

                <button
                  onClick={() => remove(job.id)}
                >
                  Remove Job
                </button>

              </div>

            </div>
          ))
        }

      </div>

    </div>
  );
}
